import React, { useState, useEffect } from 'react';
import api from '../services/api';

  const AdminDetailsModal = ({ isOpen, admin, onClose, fetchAdminData, onActionComplete }) => {
  const [actionMessage, setActionMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedAmount, setEditedAmount] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionReason, setShowRejectionReason] = useState(false);
  const [treasuryUploading, setTreasuryUploading] = useState(false);
  const [editedAmountSpent, setEditedAmountSpent] = useState('');
  
  useEffect(() => {
    // Get user role from localStorage
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  }, []);

  useEffect(() => {
    // Initialize edited amount when admin changes
    if (admin) {
      setEditedAmount(admin.amount || '');
      setEditedAmountSpent(
        admin.amount_spent ??
        admin.expenditure_incurred ??
        admin.expenditureIncurred ??
        admin.expenditureAmount ??
        admin.expenditureIncurredSoFar ??
        ''
      );
    }
  }, [admin]);

  if (!isOpen || !admin) return null;

// Function to check balance before approval
// Function to check balance before approval
const checkBalance = async (centerName, scheme, amount) => {
  try {
    setIsCheckingBalance(true);
    setBalanceError('');
    
    // Construct the API URL with center and scheme
    const balanceEndpoint = `${centerName}-${scheme}`;
    
    const response = await fetch(`${api.BASE_URL}/ledger/balance/${balanceEndpoint}`, {
      method: 'GET',
      headers: {
        'Authorization': localStorage.getItem('authToken'),
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const balanceData = await response.json();
      setBalanceData(balanceData);
      
      // Get balance amount from API response
      const availableBalance = balanceData.data && balanceData.data[0] ? parseFloat(balanceData.data[0].balance) : 0;
      const requestedAmount = parseFloat(amount) || 0;
      
      if (requestedAmount > availableBalance) {
        setBalanceError(`Insufficient balance! Available: ₹${availableBalance}, Requested: ₹${requestedAmount}`);
        return false; // Don't allow approval
      }
      
      return true; // Allow approval
    } else {
      const errorData = await response.json();
      setBalanceError('Error checking balance: ' + (errorData.message || 'Failed to fetch balance'));
      return false;
    }
  } catch (err) {
    setBalanceError('Error checking balance: ' + (err.message || 'Network error occurred'));
    console.error('Error checking balance:', err);
    return false;
  } finally {
    setIsCheckingBalance(false);
  }
};
  const generateAdminCode = (admin) => {
    return `${admin.institute || ''}/ADM/${admin.scheme || ''}/${admin.center || ''}/${admin.year || ''}/${admin.month || ''}/${admin.stitle || ''}/${admin.sno || ''}`;
  };

  // Function to safely render properties that might be objects
  const safeRender = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  };

    // Amount helper
  const toAmount = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const cleanedValue = String(value).replace(/,/g, '').trim();
    const numericValue = parseFloat(cleanedValue);

    return Number.isNaN(numericValue) ? 0 : numericValue;
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(toAmount(amount));
  };
 
  const getUploadUrl = (filePath) => {
  if (!filePath) return '';

  const cleanPath = String(filePath).trim();

  if (
    cleanPath.startsWith('http://') ||
    cleanPath.startsWith('https://')
  ) {
    return cleanPath;
  }

  const normalizedPath = cleanPath.replace(/^\/+/, '');

  if (normalizedPath.startsWith('uploads/')) {
    return `https://crd-training.in/${normalizedPath}`;
  }

  return `https://crd-training.in/uploads/${normalizedPath}`;
};

const getTreasuryDocumentPath = () => {
  const documentValue =
    admin?.treasury_bill_doc ||
    admin?.treasuryBillDoc ||
    admin?.ppa_doc ||
    '';

  if (Array.isArray(documentValue)) {
    return documentValue[0] || '';
  }

  return documentValue || '';
};

const getAdminExpenditureIncurred = () => {
  return toAmount(editedAmountSpent);
};

const getBalanceAmount = () => {
  const amountAllocated = toAmount(admin?.amount);
  const expenditureIncurred = getAdminExpenditureIncurred();

  return amountAllocated - expenditureIncurred;
};


const handleSaveAmountSpent = async () => {
  try {
    setIsLoading(true);
    setActionMessage('Saving Expenditure Incurred...');

    const response = await fetch(`${api.BASE_URL}/project/admin/${admin.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({
        amount_spent: toAmount(editedAmountSpent)
      })
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(data.message || 'Failed to save Expenditure Incurred');
    }

    admin.amount_spent = toAmount(editedAmountSpent);
    setEditedAmountSpent(String(toAmount(editedAmountSpent)));

    if (typeof fetchAdminData === 'function') {
      fetchAdminData();
    }

    setActionMessage('Expenditure Incurred saved successfully');
    setTimeout(() => setActionMessage(''), 3000);
  } catch (error) {
    console.error('Save Expenditure error:', error);
    setActionMessage(`Error: ${error.message || 'Failed to save Expenditure Incurred'}`);
    setTimeout(() => setActionMessage(''), 5000);
  } finally {
    setIsLoading(false);
  }
};

const handleTreasuryBillUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  if (file.type !== 'application/pdf') {
    setActionMessage('Error: Please select a PDF file');
    setTimeout(() => setActionMessage(''), 5000);
    event.target.value = '';
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    setActionMessage('Error: PDF file size must be less than 10MB');
    setTimeout(() => setActionMessage(''), 5000);
    event.target.value = '';
    return;
  }

  try {
    setTreasuryUploading(true);
    setActionMessage('Uploading Treasury Bill document...');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('admin_id', admin.id);
    formData.append('type', 'treasury_bill');

    const uploadResponse = await fetch(`${api.BASE_URL}/admin/upload`, {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('authToken')
      },
      body: formData
    });

    const uploadData = await uploadResponse.json();

    if (!uploadResponse.ok || uploadData.error !== false || !uploadData.file) {
      throw new Error(uploadData.message || 'Failed to upload Treasury Bill document');
    }

    const treasuryFilePath = uploadData.file;

    const updateResponse = await fetch(`${api.BASE_URL}/project/admin/${admin.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({
        treasury_bill_doc: [treasuryFilePath]
      })
    });

    const updateData = await updateResponse.json();

    if (!updateResponse.ok || updateData.error) {
      throw new Error(updateData.message || 'Failed to save Treasury Bill document');
    }

    admin.treasury_bill_doc = [treasuryFilePath];

    setActionMessage('Treasury Bill document uploaded successfully');

    if (typeof fetchAdminData === 'function') {
      fetchAdminData();
    }

    event.target.value = '';
    setTimeout(() => setActionMessage(''), 3000);
  } catch (error) {
    console.error('Treasury Bill upload error:', error);
    setActionMessage(`Error: ${error.message || 'Treasury Bill upload failed'}`);
    setTimeout(() => setActionMessage(''), 5000);
  } finally {
    setTreasuryUploading(false);
  }
};
  // Handle save
  const handleSave = async () => {
    try {
      setIsLoading(true);
      setActionMessage('Saving changes...');
      
      // Check if amount has changed
      if (editedAmount === admin.amount) {
        setActionMessage('No changes to save');
        setTimeout(() => {
          setIsEditMode(false);
        }, 1500);
        return;
      }

      // Use the same API endpoint with PUT method, sending only the changed amount
      const response = await fetch(`${api.BASE_URL}/project/admin/${admin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({ amount: editedAmount })
      });
      
      if (response.ok) {
        const data = await response.json();
        setActionMessage('Changes saved successfully!');
        setIsEditMode(false);
        // Refresh the admin list if fetchAdminData function is provided
        if (typeof fetchAdminData === 'function') {
          fetchAdminData();
        }
        // Auto-refresh page after 1.5 seconds
   // Close modal after a delay
setTimeout(() => {
  onClose();
}, 1500);
      } else {
        const errorData = await response.json();
        setActionMessage('Error: ' + (errorData.message || 'Failed to save changes'));
      }
    } catch (err) {
      setActionMessage('Error: ' + (err.message || 'Unknown error occurred'));
      console.error('Error saving changes:', err);
    } finally {
      setIsLoading(false);
    }
  };

const handleApprove = async () => {
  try {
    setIsLoading(true);
    setActionMessage('Checking balance...');
    
    // Get center name and scheme from admin data
    const centerName = admin.center; // Using 'center' field directly
    const scheme = admin.scheme; // Using 'scheme' field from admin data
    const amount = editedAmount || admin.amount;
    
    if (!centerName) {
      setActionMessage('Error: Center name not found');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }
    
    if (!scheme) {
      setActionMessage('Error: Scheme not found');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }
    
    if (!amount) {
      setActionMessage('Error: Admin task amount not found');
      setTimeout(() => setActionMessage(''), 5000);
      return;
    }
    
    // Check balance with both center and scheme
    const canApprove = await checkBalance(centerName, scheme, amount);
    
    if (!canApprove) {
      setActionMessage(balanceError || 'Cannot approve due to insufficient balance');
      setTimeout(() => setActionMessage(''), 8000);
      return;
    }
    
    // If balance check passes, proceed with approval
    setActionMessage('Approving admin task...');
    
    // Make API call to approve the admin task
    const response = await fetch(`${api.BASE_URL}/project/admin/${admin.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({ status: 'Approved' })
    });
    
    if (response.ok) {
      await response.json();
      setActionMessage('Admin task approved successfully!');
      window.dispatchEvent(new CustomEvent('refreshNotifications'));

      if (typeof onActionComplete === 'function') {
        await onActionComplete(admin, 'Approved');
      } else {
        if (typeof fetchAdminData === 'function') {
          await fetchAdminData();
        }
        setTimeout(() => onClose(), 1500);
      }
    } else {
      const errorData = await response.json();
      setActionMessage('Error: ' + (errorData.message || 'Failed to approve admin task'));
    }
  } catch (err) {
    setActionMessage('Error: ' + (err.message || 'Unknown error occurred'));
    console.error('Error approving admin task:', err);
  } finally {
    setIsLoading(false);
  }
};
  // Handle reject action
const handleReject = async () => {
  if (!rejectionReason.trim()) {
    setActionMessage('Please provide a reason for rejection');
    setTimeout(() => setActionMessage(''), 3000);
    return;
  }
  
  try {
    setIsLoading(true);
    setActionMessage('Rejecting admin task...');
    
    const response = await fetch(`${api.BASE_URL}/project/admin/${admin.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken')
      },
      body: JSON.stringify({ 
        status: 'Rejected',
        rejectionReason: rejectionReason.trim()
      })
    });
    
    if (response.ok) {
      setActionMessage('Admin task rejected successfully!');
      setShowRejectionReason(false);
      setRejectionReason('');
      if (typeof onActionComplete === 'function') {
        await onActionComplete(admin, 'Rejected');
      } else {
        if (typeof fetchAdminData === 'function') {
          await fetchAdminData();
        }
        setTimeout(() => onClose(), 1500);
      }
    } else {
      const errorData = await response.json();
      setActionMessage('Error: ' + (errorData.message || 'Failed to reject admin task'));
    }
  } catch (err) {
    setActionMessage('Error: ' + (err.message || 'Unknown error occurred'));
    console.error('Error rejecting admin task:', err);
  } finally {
    setIsLoading(false);
  }
};

  // Handle delete action
  const handleDelete = async () => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this admin task? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setActionMessage('Deleting admin task...');
      
      // Send DELETE status to the API
      const response = await fetch(`${api.BASE_URL}/project/admin/${admin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({ status: 'Deleted' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setActionMessage('Admin task deleted successfully!');
        // Refresh the admin list if fetchAdminData function is provided
        if (typeof fetchAdminData === 'function') {
          fetchAdminData();
        }
        // Auto-refresh page after 1.5 seconds
   // Close modal after a delay
setTimeout(() => {
  onClose();
}, 1500);
      } else {
        const errorData = await response.json();
        setActionMessage('Error: ' + (errorData.message || 'Failed to delete admin task'));
      }
    } catch (err) {
      setActionMessage('Error: ' + (err.message || 'Unknown error occurred'));
      console.error('Error deleting admin task:', err);
    } finally {
      setIsLoading(false);
      // Close modal after a delay
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  // For status checking
  const isApproved = admin.status === 'Approved';
  const isRejected = admin.status === 'Rejected';
  const isDeleted = admin.status === 'Deleted';

  // Create status badge color based on status
  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'Completed': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Deleted': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  // Create action message style based on message content
  const getActionMessageStyle = (message) => {
    if (message.includes('Error')) {
      return 'bg-red-50 text-red-700 border border-red-100';
    } else if (message.includes('Approving') || message.includes('Rejecting') || message.includes('Saving') || message.includes('Deleting')) {
      return 'bg-blue-50 text-blue-700 border border-blue-100';
    } else {
      return 'bg-green-50 text-green-700 border border-green-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal header - with premium styling */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-cyan-50">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold text-gray-900">Admin Task Details</h3>
            <div className={`ml-3 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(admin.status || 'Pending')}`}>
              {admin.status || 'Pending'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
            disabled={isLoading}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Action message - with enhanced styling */}
        {actionMessage && (
          <div className={`px-6 py-3 ${getActionMessageStyle(actionMessage)} rounded-md mx-6 my-3 text-center text-sm font-medium`}>
            {actionMessage}
          </div>
        )}
   {/* Rejection Reason Section for department users */}
        {userRole === 'department' && showRejectionReason && (
          <div className="mx-6 my-3">
            <div className="bg-red-50 rounded-lg border border-red-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-red-100 border-b border-red-200">
                <h4 className="font-medium text-red-800">Reason for Rejection</h4>
              </div>
              <div className="p-4">
                <label className="block text-sm font-medium text-red-700 mb-2">
                  Please provide a reason for rejecting this admin task:
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                  rows="3"
                  placeholder="Enter reason for rejection..."
                  required
                />
                <div className="mt-3 flex gap-3">
                  <button
                    onClick={handleReject}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    disabled={!rejectionReason.trim() || isLoading}
                  >
                    {isLoading ? 'Rejecting...' : 'Confirm Rejection'}
                  </button>
                  <button
                    onClick={() => {
                      setShowRejectionReason(false);
                      setRejectionReason('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Modal body - with improved spacing and card-like sections */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Admin code - with better styling */}
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm break-all border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500 mb-1 font-sans">Admin Task Code</div>
              {generateAdminCode(admin)}
            </div>
            
            {/* Admin details - with card styling */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Task Information</h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Title</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.title)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Type</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.type)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Institute</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.institute)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Training Type</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.training_type)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Scheme and Center Information */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Scheme and Center</h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Scheme</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.scheme)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Scheme Name</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.scheme_name)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Center</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.center)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Center Name</h4>
                    <p className="mt-1 text-gray-900">{safeRender(admin.center_name)}</p>
                  </div>
                </div>
              </div>
            </div>
{/* Additional Details */}
<div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
    <h4 className="font-medium text-gray-700">Additional Details</h4>
  </div>
  <div className="p-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h4 className="text-sm font-medium text-gray-500">Year</h4>
        <p className="mt-1 text-gray-900">{safeRender(admin.year)}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500">Month</h4>
        <p className="mt-1 text-gray-900">{safeRender(admin.month)}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500">A.S Date</h4>
        <p className="mt-1 text-gray-900">{admin.asDate ? new Date(admin.asDate).toLocaleDateString() : 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500">A.S Number</h4>
        <p className="mt-1 text-gray-900">{safeRender(admin.asNumber)}</p>
      </div>
      
      {/* A.S File - Only visible for department users */}
      {userRole === 'department' && (
        <div>
          <h4 className="text-sm font-medium text-gray-500">A.S File</h4>
          {admin.asFile && admin.asFile.trim() !== '' ? (
            <a 
              href={`https://crd-training.in/uploads/${admin.asFile}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="mt-1 text-blue-600 hover:text-blue-800 underline flex items-center"
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
               A.S File
            </a>
          ) : (
            <p className="mt-1 text-gray-900">No file uploaded</p>
          )}
        </div>
      )}
      
    <div>
  <h4 className="text-sm font-medium text-gray-500">Short Title</h4>
  <p className="mt-1 text-gray-900">{safeRender(admin.stitle)}</p>
</div>
<div>
  <h4 className="text-sm font-medium text-gray-500">Serial Number</h4>
  <p className="mt-1 text-gray-900">{safeRender(admin.sno)}</p>
</div>

{/* Conditionally show scheme_type and position only for SIR institute */}
{admin.institute === 'SIR' && (
  <>
    <div>
      <h4 className="text-sm font-medium text-gray-500">Scheme Type</h4>
      <p className="mt-1 text-gray-900">
        {admin.scheme_type && admin.scheme_type.trim() !== '' ? admin.scheme_type : 'N/A'}
      </p>
    </div>
    <div>
      <h4 className="text-sm font-medium text-gray-500">Position</h4>
      <p className="mt-1 text-gray-900">
        {admin.position && admin.position.trim() !== '' ? admin.position : 'N/A'}
      </p>
    </div>
  </>
)}
      <div>
        <h4 className="text-sm font-medium text-gray-500">Amount</h4>
        {isEditMode ? (
          <input
            type="number"
            value={editedAmount}
            onChange={(e) => setEditedAmount(e.target.value)}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Enter amount"
          />
        ) : (
          <p className="mt-1 text-gray-900">{formatCurrency(admin.amount)}</p>
        )}
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-500">Created By</h4>
        <p className="mt-1 text-gray-900">{safeRender(admin.created_by)}</p>
      </div>
    </div>
  </div>
</div>
         

{/* Treasury Bill Document */}
<div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
    <h4 className="font-medium text-gray-700">Treasury Bill Document</h4>
  </div>

  <div className="p-4">
    {getTreasuryDocumentPath() ? (
      <a
        href={getUploadUrl(getTreasuryDocumentPath())}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline text-sm"
      >
        View Treasury Bill Document
      </a>
    ) : (
      <p className="text-sm text-gray-500 mb-3">No document uploaded</p>
    )}

    {userRole !== 'department' && isApproved && !isDeleted && !getTreasuryDocumentPath() && (
      <div className="mt-3">
        <label className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer">
          {treasuryUploading ? 'Uploading...' : 'Upload Treasury Bill Document'}
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleTreasuryBillUpload}
            disabled={treasuryUploading || isLoading}
            className="hidden"
          />
        </label>
      </div>
    )}
  </div>
</div>

{/* Financial Summary */}
<div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
    <h4 className="font-medium text-gray-700">Financial Summary</h4>
  </div>

  <div className="p-4">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
        <h4 className="text-sm font-medium text-blue-700">Amount Allocated</h4>
        <p className="mt-1 text-blue-900 font-semibold">
          {formatCurrency(admin.amount)}
        </p>
      </div>

      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
        <h4 className="text-sm font-medium text-orange-700">Expenditure Incurred</h4>

        <input
          type="number"
          min="0"
          value={editedAmountSpent}
          onChange={(event) => setEditedAmountSpent(event.target.value)}
          className="mt-2 w-full rounded-md border-orange-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          placeholder="Enter Expenditure Incurred"
          disabled={userRole === 'department' || !isApproved || isDeleted || isLoading}
        />

        <p className="mt-2 text-orange-900 font-semibold">
          {formatCurrency(getAdminExpenditureIncurred())}
        </p>

        {userRole !== 'department' && isApproved && !isDeleted && (
          <button
            type="button"
            onClick={handleSaveAmountSpent}
            className="mt-3 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Expenditure'}
          </button>
        )}
      </div>

      <div className="bg-green-50 rounded-lg p-4 border border-green-100">
        <h4 className="text-sm font-medium text-green-700">Balance Amount</h4>
        <p className="mt-1 text-green-900 font-semibold">
          {formatCurrency(getBalanceAmount())}
        </p>
      </div>
    </div>
  </div>
</div>

{/* Balance Information - shown when checking balance */}
{balanceData && (
  <div className="bg-blue-50 rounded-lg border border-blue-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 bg-blue-100 border-b border-blue-200">
      <h4 className="font-medium text-blue-800">Balance Information</h4>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-blue-600">Available Balance</h4>
      <p className="mt-1 text-blue-900 font-semibold">₹{balanceData.data && balanceData.data[0] ? balanceData.data[0].balance : 0}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-blue-600">Requested Amount</h4>
          <p className="mt-1 text-blue-900 font-semibold">₹{editedAmount || admin.amount}</p>
        </div>
      </div>
    </div>
  </div>
)}
</div>
  </div>
        {/* Modal footer with action buttons - with premium styling */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
            disabled={isLoading}
          >
            Close
          </button>
          
          {/* Edit and Save buttons for department users */}
        {userRole === 'department' && !isDeleted && !isApproved && (
            <>
              {!isEditMode ? (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  disabled={isLoading}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditMode(false);
                      setEditedAmount(admin.amount || '');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm transition-colors"
                    disabled={isLoading}
                  >
                    Cancel Edit
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : 'Save'}
                  </button>
                </>
              )}
            </>
          )}
          
          {/* Delete button - visible for department users when not in edit mode and not already deleted */}
   {/* Delete button - visible for department users when not in edit mode, not already deleted, and not approved */}
{userRole === 'department' && !isEditMode && !isDeleted && isApproved && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm transition-colors"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </span>
              ) : 'Delete'}
            </button>
          )}
          
          {/* Approve/Reject buttons only visible when not in edit mode and not deleted */}
          {userRole === 'department' && !isApproved && !isRejected && !isDeleted && !isEditMode && (
            <>
 <button
  onClick={() => setShowRejectionReason(true)}
  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
  disabled={isLoading}
>
  Reject
</button>
              
      <button
  onClick={handleApprove}
  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
  disabled={isLoading || isCheckingBalance}
>
  {isLoading || isCheckingBalance ? (
    <span className="flex items-center">
      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {isCheckingBalance ? 'Checking Balance...' : 'Processing...'}
    </span>
  ) : 'Approve'}
</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDetailsModal;